import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const AuthorizationTable = ({ onSelectionChange, onCreditAction }) => {
  const [selectedCredits, setSelectedCredits] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const mockCredits = [
    {
      id: 'VCS-2847-2023-001',
      serialNumber: 'VCS-2847-2023-001-001-001-VCU',
      hostCountry: 'Brazil',
      projectName: 'Amazon Rainforest Conservation',
      vintage: 2023,
      quantity: 1000,
      authorizationStatus: 'authorized',
      caRequired: true,
      caStatus: 'pending',
      ndcAlignment: 'aligned',
      lastUpdated: '2025-01-15T10:30:00Z',
      authorizedBy: 'Ministry of Environment - Brazil',
      complianceScore: 98
    },
    {
      id: 'GS-1456-2023-002',
      serialNumber: 'GS-1456-2023-002-001-001-VER',
      hostCountry: 'Colombia',
      projectName: 'Renewable Energy Development',
      vintage: 2023,
      quantity: 2500,
      authorizationStatus: 'pending',
      caRequired: true,
      caStatus: 'not_required',
      ndcAlignment: 'aligned',
      lastUpdated: '2025-01-20T14:45:00Z',
      authorizedBy: 'Pending Review',
      complianceScore: 95
    },
    {
      id: 'ACR-789-2022-003',
      serialNumber: 'ACR-789-2022-003-001-001-CRT',
      hostCountry: 'Peru',
      projectName: 'Forest Management Initiative',
      vintage: 2022,
      quantity: 750,
      authorizationStatus: 'rejected',
      caRequired: false,
      caStatus: 'not_applicable',
      ndcAlignment: 'conflict',
      lastUpdated: '2025-01-18T09:15:00Z',
      authorizedBy: 'Authorization Denied',
      complianceScore: 72
    },
    {
      id: 'VCS-3421-2024-004',
      serialNumber: 'VCS-3421-2024-004-001-001-VCU',
      hostCountry: 'Chile',
      projectName: 'Solar Power Installation',
      vintage: 2024,
      quantity: 1800,
      authorizationStatus: 'authorized',
      caRequired: true,
      caStatus: 'completed',
      ndcAlignment: 'aligned',
      lastUpdated: '2025-01-21T16:20:00Z',
      authorizedBy: 'National Energy Commission - Chile',
      complianceScore: 99
    },
    {
      id: 'GS-2134-2023-005',
      serialNumber: 'GS-2134-2023-005-001-001-VER',
      hostCountry: 'Costa Rica',
      projectName: 'Sustainable Agriculture Program',
      vintage: 2023,
      quantity: 1200,
      authorizationStatus: 'pending',
      caRequired: true,
      caStatus: 'pending',
      ndcAlignment: 'aligned',
      lastUpdated: '2025-01-19T11:30:00Z',
      authorizedBy: 'Under Review',
      complianceScore: 94
    }
  ];

  const getStatusBadge = (status, type = 'authorization') => {
    const statusConfig = {
      authorization: {
        authorized: { color: 'text-success bg-success/10', icon: 'CheckCircle', label: 'Authorized' },
        pending: { color: 'text-warning bg-warning/10', icon: 'Clock', label: 'Pending' },
        rejected: { color: 'text-error bg-error/10', icon: 'XCircle', label: 'Rejected' }
      },
      ca: {
        completed: { color: 'text-success bg-success/10', icon: 'CheckCircle', label: 'Completed' },
        pending: { color: 'text-warning bg-warning/10', icon: 'Clock', label: 'Pending' },
        not_required: { color: 'text-muted-foreground bg-muted', icon: 'Minus', label: 'Not Required' },
        not_applicable: { color: 'text-muted-foreground bg-muted', icon: 'Minus', label: 'N/A' }
      },
      ndc: {
        aligned: { color: 'text-success bg-success/10', icon: 'Shield', label: 'Aligned' },
        conflict: { color: 'text-error bg-error/10', icon: 'AlertTriangle', label: 'Conflict' },
        pending: { color: 'text-warning bg-warning/10', icon: 'Clock', label: 'Pending' }
      }
    };

    const config = statusConfig?.[type]?.[status] || statusConfig?.[type]?.['pending'];
    
    return (
      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon name={config?.icon} size={12} />
        <span>{config?.label}</span>
      </span>
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = mockCredits?.map(credit => credit?.id);
      setSelectedCredits(allIds);
      onSelectionChange(allIds);
    } else {
      setSelectedCredits([]);
      onSelectionChange([]);
    }
  };

  const handleSelectCredit = (creditId, checked) => {
    let newSelection;
    if (checked) {
      newSelection = [...selectedCredits, creditId];
    } else {
      newSelection = selectedCredits?.filter(id => id !== creditId);
    }
    setSelectedCredits(newSelection);
    onSelectionChange(newSelection);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedCredits = React.useMemo(() => {
    if (!sortConfig?.key) return mockCredits;

    return [...mockCredits]?.sort((a, b) => {
      if (a?.[sortConfig?.key] < b?.[sortConfig?.key]) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (a?.[sortConfig?.key] > b?.[sortConfig?.key]) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [sortConfig]);

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 border-b border-border">
        <h3 className="text-lg font-semibold text-card-foreground">International Transfer Eligible Credits</h3>
        <p className="text-sm text-muted-foreground">Credits with host-country authorization status and CA requirements</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 w-12">
                <Checkbox
                  checked={selectedCredits?.length === mockCredits?.length}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('serialNumber')}
                  className="flex items-center space-x-1 hover:text-card-foreground"
                >
                  <span>Serial Number</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('hostCountry')}
                  className="flex items-center space-x-1 hover:text-card-foreground"
                >
                  <span>Host Country</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Project</th>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                <button
                  onClick={() => handleSort('quantity')}
                  className="flex items-center space-x-1 hover:text-card-foreground"
                >
                  <span>Quantity</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Authorization</th>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">CA Status</th>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">NDC Alignment</th>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Compliance</th>
              <th className="text-left p-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedCredits?.map((credit) => (
              <tr key={credit?.id} className="hover:bg-muted/30 transition-colors">
                <td className="p-4">
                  <Checkbox
                    checked={selectedCredits?.includes(credit?.id)}
                    onChange={(e) => handleSelectCredit(credit?.id, e?.target?.checked)}
                  />
                </td>
                <td className="p-4">
                  <div className="font-mono text-sm text-card-foreground">{credit?.serialNumber}</div>
                  <div className="text-xs text-muted-foreground">Vintage: {credit?.vintage}</div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-4 bg-muted rounded-sm flex items-center justify-center">
                      <span className="text-xs font-bold">{credit?.hostCountry?.slice(0, 2)?.toUpperCase()}</span>
                    </div>
                    <span className="text-sm font-medium text-card-foreground">{credit?.hostCountry}</span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="text-sm font-medium text-card-foreground max-w-48 truncate">{credit?.projectName}</div>
                  <div className="text-xs text-muted-foreground">Updated: {formatDate(credit?.lastUpdated)}</div>
                </td>
                <td className="p-4">
                  <div className="text-sm font-medium text-card-foreground">{credit?.quantity?.toLocaleString()}</div>
                  <div className="text-xs text-muted-foreground">tCO2e</div>
                </td>
                <td className="p-4">
                  {getStatusBadge(credit?.authorizationStatus, 'authorization')}
                  <div className="text-xs text-muted-foreground mt-1">{credit?.authorizedBy}</div>
                </td>
                <td className="p-4">
                  {getStatusBadge(credit?.caStatus, 'ca')}
                </td>
                <td className="p-4">
                  {getStatusBadge(credit?.ndcAlignment, 'ndc')}
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${
                      credit?.complianceScore >= 95 ? 'bg-success' : 
                      credit?.complianceScore >= 80 ? 'bg-warning' : 'bg-error'
                    }`}></div>
                    <span className="text-sm font-medium text-card-foreground">{credit?.complianceScore}%</span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="FileCheck"
                      onClick={() => onCreditAction('request_authorization', credit?.id)}
                      disabled={credit?.authorizationStatus === 'authorized'}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="RefreshCw"
                      onClick={() => onCreditAction('update_ca_status', credit?.id)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="FileText"
                      onClick={() => onCreditAction('generate_report', credit?.id)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {mockCredits?.length} credits</span>
          <div className="flex items-center space-x-2">
            <span>Real-time sync:</span>
            <div className="flex items-center space-x-1">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
              <span className="text-success">Active</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorizationTable;